import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft,
  Video,
  Shield,
  Clock,
  FileText,
  Mic,
  Lock,
  Trash2,
  Lightbulb,
  CheckCircle,
  ExternalLink,
  Star,
} from 'lucide-react'
import { sessionsApi } from '../../lib/api/sessions'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { useProfileTimezone } from '../../lib/hooks/useProfileTimezone'
import { formatDateInTz, formatTimeInTz } from '../../lib/utils/timezone'
import { decodeId } from '../../lib/utils/idObfuscation'

interface SessionData {
  id: number
  title: string
  interviewer_alias?: string
  scheduled_at: string
  duration_minutes: number
  status: string
  zoom_join_url?: string
  zoom_password?: string
}

interface BookingData {
  booking_id: number
  interviewer_alias?: string
  scheduled_start_utc: string
  scheduled_end_utc?: string
  duration_minutes?: number
  status: string
  zoom_join_url?: string
  zoom_password?: string
  booking_ref?: string
}

const TIPS = [
  'Speak clearly and at a moderate pace',
  'Take a moment to think before answering',
  'Ask clarifying questions if needed',
]

export default function ClientSession() {
  const navigate = useNavigate()
  const { id, source, sourceId } = useParams<{ id?: string; source?: string; sourceId?: string }>()
  const timezone = useProfileTimezone()

  const [zoomUrl, setZoomUrl] = useState<string | null>(null)
  const [zoomPassword, setZoomPassword] = useState<string | null>(null)
  const [interviewerAlias, setInterviewerAlias] = useState<string | null>(null)
  const [scheduledAt, setScheduledAt] = useState<string | null>(null)
  const [duration, setDuration] = useState<number>(60)
  const [status, setStatus] = useState('confirmed')
  const [loading, setLoading] = useState(true)
  const [retentionDays, setRetentionDays] = useState(30)
  const [connected, setConnected] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Pre-join modal
  const [showPreJoin, setShowPreJoin] = useState(false)
  const [micStatus, setMicStatus] = useState<'idle' | 'checking' | 'ok' | 'denied' | 'error'>('idle')
  const [camStatus, setCamStatus] = useState<'idle' | 'checking' | 'ok' | 'denied' | 'error'>('idle')

  // Rating state (shown after session)
  const [showRating, setShowRating] = useState(false)
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [ratingComment, setRatingComment] = useState('')
  const [submittingRating, setSubmittingRating] = useState(false)
  const [alreadyRated, setAlreadyRated] = useState(false)

  useEffect(() => {
    const resolvedSource = source ?? 'session'
    const resolvedId = sourceId ?? id

    if (!resolvedId) { setLoading(false); return }

    const numId = decodeId(resolvedId) ?? parseInt(resolvedId, 10)

    const applyBookingData = (data: BookingData) => {
      setZoomUrl(data.zoom_join_url ?? null)
      setZoomPassword(data.zoom_password ?? null)
      setInterviewerAlias(data.interviewer_alias ?? null)
      setScheduledAt(data.scheduled_start_utc)
      if (data.scheduled_end_utc) {
        const mins = Math.round(
          (new Date(data.scheduled_end_utc).getTime() - new Date(data.scheduled_start_utc).getTime()) / 60000
        )
        setDuration(mins)
      } else {
        setDuration(data.duration_minutes ?? 60)
      }
      setStatus(data.status)
      // Z-2 fix: use the raw data value — not the stale scheduledAt state variable
      const sessionDate = new Date(data.scheduled_start_utc)
      const deleteAt = new Date(sessionDate)
      deleteAt.setDate(deleteAt.getDate() + 30)
      setRetentionDays(Math.max(0, Math.ceil((deleteAt.getTime() - Date.now()) / 86400000)))
    }

    const applySessionData = (data: SessionData) => {
      setZoomUrl(data.zoom_join_url ?? null)
      setZoomPassword(data.zoom_password ?? null)
      setInterviewerAlias(data.interviewer_alias ?? null)
      setScheduledAt(data.scheduled_at)
      setDuration(data.duration_minutes)
      setStatus(data.status)
      // Z-2 fix: same — use raw data.scheduled_at
      const sessionDate = new Date(data.scheduled_at)
      const deleteAt = new Date(sessionDate)
      deleteAt.setDate(deleteAt.getDate() + 30)
      setRetentionDays(Math.max(0, Math.ceil((deleteAt.getTime() - Date.now()) / 86400000)))
    }

    const load = async (isInitial = false) => {
      try {
        if (resolvedSource === 'booking') {
          const data: BookingData = await sessionsApi.getBooking(numId)
          applyBookingData(data)
        } else {
          const data: SessionData = await sessionsApi.getSession(numId)
          applySessionData(data)
          if (isInitial && data.status === 'completed') {
            try {
              const r = await sessionsApi.getRatingForSession(numId)
              if (r) setAlreadyRated(true)
            } catch { /* not yet rated */ }
          }
        }
      } catch (err) {
        if (isInitial) {
          // F-3 fix: surface load failure — don't silently show a blank page
          toast.error(err instanceof Error ? err.message : 'Failed to load session details.')
        }
      } finally {
        if (isInitial) setLoading(false)
      }
    }

    load(true)

    // O-3 fix: poll every 60 s so the Zoom button state and status badge stay
    // accurate even when the user leaves the tab open for an extended period.
    pollRef.current = setInterval(() => load(false), 60_000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [id, source, sourceId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnectZoom = () => {
    if (canJoinZoom) {
      setShowPreJoin(true)
      setMicStatus('idle')
      setCamStatus('idle')
    }
  }

  const handleCheckDevices = async () => {
    setMicStatus('checking')
    setCamStatus('checking')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true })
      stream.getTracks().forEach((t) => t.stop())
      setMicStatus('ok')
      setCamStatus('ok')
    } catch (err: unknown) {
      const name = err instanceof Error ? (err as { name?: string }).name : ''
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setMicStatus('denied')
        setCamStatus('denied')
      } else if (name === 'NotFoundError') {
        // Try audio only
        try {
          const a = await navigator.mediaDevices.getUserMedia({ audio: true })
          a.getTracks().forEach((t) => t.stop())
          setMicStatus('ok')
        } catch {
          setMicStatus('denied')
        }
        setCamStatus('error')
      } else {
        setMicStatus('error')
        setCamStatus('error')
      }
    }
  }

  const openZoomApp = () => {
    if (!zoomUrl) return
    const idMatch = zoomUrl.match(/\/j\/(\d+)/)
    const pwdMatch = zoomUrl.match(/[?&]pwd=([^&]+)/)
    if (idMatch) {
      const confno = idMatch[1]
      const pwd = pwdMatch ? pwdMatch[1] : ''
      window.location.href = `zoommtg://zoom.us/join?action=join&confno=${confno}${pwd ? `&pwd=${pwd}` : ''}`
      setTimeout(() => {
        // Fallback: if app didn't open after 2s, open web URL
        window.open(zoomUrl, '_blank', 'noopener,noreferrer')
      }, 2000)
    } else {
      window.open(zoomUrl, '_blank', 'noopener,noreferrer')
    }
    setConnected(true)
    setShowPreJoin(false)
  }

  const openZoomBrowser = () => {
    if (zoomUrl) {
      window.open(zoomUrl, '_blank', 'noopener,noreferrer')
      setConnected(true)
      setShowPreJoin(false)
    }
  }

  const handleSubmitRating = async () => {
    if (rating === 0) return
    const resolvedSource = (source ?? 'session') as 'session' | 'booking'
    const numId = parseInt((sourceId ?? id)!, 10)
    setSubmittingRating(true)
    try {
      await sessionsApi.submitRating(resolvedSource, numId, rating, ratingComment || undefined)
      setAlreadyRated(true)
      setShowRating(false)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to submit rating.')
    } finally {
      setSubmittingRating(false)
    }
  }

  const fmtDate = (iso: string) => formatDateInTz(iso, timezone)
  const fmtTime = (iso: string) => formatTimeInTz(iso, timezone)

  if (loading) return <LoadingSpinner message="Loading session..." />

  const isCompleted = status === 'completed'

  // End time = scheduled start + duration (in ms)
  const endTime = scheduledAt
    ? new Date(scheduledAt).getTime() + duration * 60 * 1000
    : null

  // Session is ended if backend says completed OR local clock has passed end time
  const isSessionEnded = isCompleted || (endTime !== null && Date.now() > endTime)

  // Enable the Zoom button only within 45 min before start and before end time
  const canJoinZoom = (() => {
    if (!zoomUrl || !scheduledAt || isSessionEnded) return false
    const meetingTime = new Date(scheduledAt).getTime()
    const now = Date.now()
    const msUntilMeeting = meetingTime - now
    return msUntilMeeting <= 45 * 60 * 1000  // within 45 min before start
  })()

  return (
    <div className="cs-page">

      {/* ── Header ── */}
      <div className="cs-header">
        <button
          className="cs-back-btn"
          type="button"
          onClick={() => navigate('/client/interviews')}
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <h1 className="cs-heading">Interview Session</h1>
        <span
          className="cs-status-pill"
          style={{
            background: isSessionEnded ? 'rgba(22,163,74,0.1)' : 'rgba(37,99,235,0.1)',
            color: isSessionEnded ? '#16a34a' : '#2563eb',
          }}
        >
          {isSessionEnded ? <CheckCircle size={12} /> : <Clock size={12} />}
          {isSessionEnded ? 'Completed' : status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </div>

      {scheduledAt && (
        <div className="cs-date-bar">
          <Clock size={14} />
          <span>{fmtDate(scheduledAt)}</span>
          <span className="cs-date-sep">·</span>
          <span>{fmtTime(scheduledAt)}</span>
          <span className="cs-date-sep">·</span>
          <span>{duration} min</span>
        </div>
      )}

      {/* ── Main layout ── */}
      <div className="cs-layout">

        {/* ── Left / Main column ── */}
        <div className="cs-main">

          {/* Video Conference Area */}
          <div className="cs-video-area">
            <div className="cs-video-label">
              <Video size={14} />
              Video Conference Area
            </div>

            <div className="cs-video-inner">
              {/* Zoom icon */}
              <div className="cs-zoom-icon-wrap">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <rect width="48" height="48" rx="12" fill="#2D8CFF" />
                  <path d="M8 16.5C8 14.567 9.567 13 11.5 13H27C28.933 13 30.5 14.567 30.5 16.5V28.5C30.5 30.433 28.933 32 27 32H11.5C9.567 32 8 30.433 8 28.5V16.5Z" fill="white"/>
                  <path d="M32 20.5L40 15V33L32 27.5V20.5Z" fill="white"/>
                </svg>
              </div>

              <h2 className="cs-video-title">Launch Zoom Meeting</h2>
              <p className="cs-video-desc">
                Click "Connect to Zoom" to open the meeting.<br />
                For best results, use the <strong style={{ color: '#93c5fd' }}>Zoom desktop app</strong>.
              </p>

              {/* Identity protected */}
              <div className="cs-identity-notice">
                <Shield size={15} style={{ flexShrink: 0 }} />
                <span>
                  <strong>Identity Protected:</strong> Your interviewer's identity is protected.
                  You are connected with a professional interview coach.
                </span>
              </div>
            </div>
          </div>

          {/* Recording & Transcript */}
          <div className="cs-card">
            <div className="cs-card-header">
              <Video size={16} style={{ color: 'var(--primary-navy)' }} />
              <h3>Recording &amp; Transcript</h3>
            </div>
            <div className="cs-detail-grid">
              <div className="cs-detail-row">
                <span className="cs-detail-label">Zoom Meeting:</span>
                <span className="cs-detail-val">
                  <ExternalLink size={13} style={{ color: '#2563eb' }} />
                  Opens via Zoom app (not embedded)
                </span>
              </div>
              <div className="cs-detail-row">
                <span className="cs-detail-label">Transcript Status:</span>
                <span className="cs-detail-val">
                  <Mic size={13} style={{ color: connected ? '#16a34a' : '#9ca3af' }} />
                  {connected ? 'Connected' : 'Awaiting connection'}
                </span>
              </div>
              <div className="cs-detail-row">
                <span className="cs-detail-label">Recording Storage:</span>
                <span className="cs-detail-val">
                  <Lock size={13} style={{ color: '#7c3aed' }} />
                  Secure encrypted storage
                </span>
              </div>
              <div className="cs-detail-row">
                <span className="cs-detail-label">Auto-Delete:</span>
                <span className={`cs-detail-val ${retentionDays <= 7 ? 'cs-urgent' : ''}`}>
                  <Trash2 size={13} />
                  {retentionDays} days until auto-delete
                </span>
              </div>
            </div>
          </div>

          {/* Rating (completed sessions) */}
          {isSessionEnded && (
            <div className="cs-card">
              <div className="cs-card-header">
                <Star size={16} style={{ color: '#d4af37' }} />
                <h3>Rate Your Interviewer</h3>
              </div>
              {alreadyRated ? (
                <div className="cs-rated-msg">
                  <CheckCircle size={16} style={{ color: '#16a34a' }} />
                  You have already rated this session. Thank you!
                </div>
              ) : !showRating ? (
                <button
                  className="btn btn-outline"
                  onClick={() => setShowRating(true)}
                  style={{ borderColor: '#d4af37', color: '#b8960c' }}
                >
                  <Star size={15} /> Rate Interviewer
                </button>
              ) : (
                <div>
                  <div className="cs-stars">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button
                        key={s}
                        type="button"
                        className="cs-star-btn"
                        onClick={() => setRating(s)}
                        onMouseEnter={() => setHoverRating(s)}
                        onMouseLeave={() => setHoverRating(0)}
                      >
                        <Star
                          size={28}
                          fill={s <= (hoverRating || rating) ? '#d4af37' : 'none'}
                          stroke={s <= (hoverRating || rating) ? '#d4af37' : '#cbd5e1'}
                        />
                      </button>
                    ))}
                  </div>
                  <textarea
                    className="form-input"
                    placeholder="Comments (optional)..."
                    value={ratingComment}
                    onChange={(e) => setRatingComment(e.target.value)}
                    rows={2}
                    style={{ marginTop: 12, resize: 'vertical' }}
                  />
                  <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                    <button
                      className="btn btn-primary"
                      onClick={handleSubmitRating}
                      disabled={submittingRating || rating === 0}
                    >
                      {submittingRating ? 'Submitting…' : 'Submit Rating'}
                    </button>
                    <button className="btn btn-outline" onClick={() => setShowRating(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Right column ── */}
        <div className="cs-sidebar">

          {/* Connect to Zoom button */}
          {isSessionEnded ? (
            <div className="cs-connect-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'rgba(22,163,74,0.08)', borderRadius: 10, border: '1px solid rgba(22,163,74,0.2)' }}>
                <CheckCircle size={18} style={{ color: '#16a34a', flexShrink: 0 }} />
                <div>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#15803d' }}>Session Ended</p>
                  <p style={{ margin: 0, fontSize: 12, color: '#6b7280', marginTop: 2 }}>This meeting has concluded. Check below to rate your interviewer.</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="cs-connect-card">
              {zoomPassword && (
                <div className="cs-pwd-row">
                  <Lock size={13} />
                  <span>Meeting Password:</span>
                  <strong>{zoomPassword}</strong>
                </div>
              )}
              <button
                className="cs-connect-btn"
                onClick={handleConnectZoom}
                disabled={!canJoinZoom}
              >
                <Video size={18} />
                {connected ? 'Rejoin Zoom Meeting' : 'Connect to Zoom'}
              </button>
              {!canJoinZoom && (
                <p className="cs-no-link">Available 45 minutes before your session starts.</p>
              )}
              {connected && (
                <p className="cs-connected-msg">
                  <CheckCircle size={13} style={{ color: '#16a34a' }} />
                  Zoom opened — join from your Zoom app
                </p>
              )}
            </div>
          )}

          {/* Interview Tips */}
          <div className="cs-card">
            <div className="cs-card-header">
              <Lightbulb size={16} style={{ color: '#d97706' }} />
              <h3>Interview Tips</h3>
            </div>
            <ul className="cs-tips">
              {TIPS.map((tip, i) => (
                <li key={i}>
                  <span className="cs-tip-dot" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>

          {/* Session Info */}
          <div className="cs-card">
            <div className="cs-card-header">
              <FileText size={16} style={{ color: 'var(--primary-navy)' }} />
              <h3>Session Info</h3>
            </div>
            <div className="cs-session-info-rows">
              <div className="cs-session-info-row">
                <span className="cs-session-info-label">Interviewer</span>
                <span className="cs-session-info-val">
                  {interviewerAlias ?? <span style={{ color: '#9ca3af' }}>—</span>}
                </span>
              </div>
              {scheduledAt && (
                <div className="cs-session-info-row">
                  <span className="cs-session-info-label">Date</span>
                  <span className="cs-session-info-val">{fmtDate(scheduledAt)}</span>
                </div>
              )}
              {scheduledAt && (
                <div className="cs-session-info-row">
                  <span className="cs-session-info-label">Time</span>
                  <span className="cs-session-info-val">{fmtTime(scheduledAt)}</span>
                </div>
              )}
              <div className="cs-session-info-row">
                <span className="cs-session-info-label">Duration</span>
                <span className="cs-session-info-val">{duration} min</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Pre-Join Modal ── */}
      {showPreJoin && (
        <div className="pj-overlay" onClick={() => setShowPreJoin(false)}>
          <div className="pj-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="pj-title">
              <svg width="24" height="24" viewBox="0 0 48 48" fill="none" style={{ flexShrink: 0 }}>
                <rect width="48" height="48" rx="10" fill="#2D8CFF" />
                <path d="M8 16.5C8 14.567 9.567 13 11.5 13H27C28.933 13 30.5 14.567 30.5 16.5V28.5C30.5 30.433 28.933 32 27 32H11.5C9.567 32 8 30.433 8 28.5V16.5Z" fill="white"/>
                <path d="M32 20.5L40 15V33L32 27.5V20.5Z" fill="white"/>
              </svg>
              Join Zoom Meeting
            </h2>

            {/* Recommended: Desktop App */}
            <div className="pj-option pj-option-primary">
              <div className="pj-option-header">
                <span className="pj-badge">Recommended</span>
                <span className="pj-option-title">Open with Zoom Desktop App</span>
              </div>
              <p className="pj-option-desc">
                Best quality — no browser microphone issues. Make sure Zoom is installed.
              </p>
              <button className="pj-btn-primary" onClick={openZoomApp}>
                <Video size={16} />
                Open Zoom App
              </button>
              <p className="pj-install-note">
                Don't have Zoom?{' '}
                <a href="https://zoom.us/download" target="_blank" rel="noopener noreferrer">
                  Download free ↗
                </a>
              </p>
            </div>

            {/* Alternative: Browser */}
            <div className="pj-option">
              <div className="pj-option-header">
                <span className="pj-option-title">Join via Web Browser</span>
              </div>
              <p className="pj-option-desc">
                Check your microphone and camera access before joining.
              </p>

              {/* Device check */}
              <div className="pj-device-check">
                <div className="pj-device-row">
                  <Mic size={15} />
                  <span>Microphone</span>
                  {micStatus === 'idle' && <span className="pj-status pj-idle">Not checked</span>}
                  {micStatus === 'checking' && <span className="pj-status pj-checking">Checking…</span>}
                  {micStatus === 'ok' && <span className="pj-status pj-ok">✓ Allowed</span>}
                  {micStatus === 'denied' && <span className="pj-status pj-denied">✗ Blocked</span>}
                  {micStatus === 'error' && <span className="pj-status pj-denied">Not found</span>}
                </div>
                <div className="pj-device-row">
                  <Video size={15} />
                  <span>Camera</span>
                  {camStatus === 'idle' && <span className="pj-status pj-idle">Not checked</span>}
                  {camStatus === 'checking' && <span className="pj-status pj-checking">Checking…</span>}
                  {camStatus === 'ok' && <span className="pj-status pj-ok">✓ Allowed</span>}
                  {camStatus === 'denied' && <span className="pj-status pj-denied">✗ Blocked</span>}
                  {camStatus === 'error' && <span className="pj-status pj-idle">Not found</span>}
                </div>
              </div>

              {(micStatus === 'denied' || camStatus === 'denied') && (
                <div className="pj-fix-steps">
                  <strong>Fix microphone access:</strong>
                  <ol>
                    <li>Click the <strong>lock icon</strong> in your browser's address bar</li>
                    <li>Set <strong>Microphone</strong> and <strong>Camera</strong> to <strong>Allow</strong></li>
                    <li>Refresh this page and try again</li>
                  </ol>
                </div>
              )}

              <div className="pj-browser-btns">
                <button className="pj-btn-check" onClick={handleCheckDevices} disabled={micStatus === 'checking'}>
                  {micStatus === 'checking' ? 'Checking…' : 'Check Mic & Camera'}
                </button>
                <button
                  className="pj-btn-browser"
                  onClick={openZoomBrowser}
                  disabled={micStatus === 'denied'}
                >
                  Join in Browser
                </button>
              </div>
            </div>

            <button className="pj-close" onClick={() => setShowPreJoin(false)}>✕ Cancel</button>
          </div>
        </div>
      )}

      <style>{`
        .cs-page {
          max-width: 1200px;
        }

        /* ── Header ── */
        .cs-header {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 10px;
        }

        .cs-back-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 7px 14px;
          background: none;
          border: 1px solid var(--border);
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          color: var(--text-dark);
          cursor: pointer;
          transition: all 0.15s;
        }

        .cs-back-btn:hover {
          background: #f3f4f6;
          border-color: #9ca3af;
        }

        .cs-heading {
          font-size: 22px;
          font-weight: 700;
          flex: 1;
          color: var(--text-dark);
        }

        .cs-status-pill {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 5px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .cs-date-bar {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 16px;
          background: #f8f9fb;
          border-radius: 8px;
          font-size: 13.5px;
          color: var(--text-muted);
          margin-bottom: 22px;
          border: 1px solid var(--border);
        }

        .cs-date-sep {
          color: #d1d5db;
        }

        /* ── Layout ── */
        .cs-layout {
          display: grid;
          grid-template-columns: 1fr 300px;
          gap: 22px;
          align-items: start;
        }

        .cs-main {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }

        .cs-sidebar {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        /* ── Video area ── */
        .cs-video-area {
          border-radius: 14px;
          overflow: hidden;
          border: 1px solid #2d3a4a;
        }

        .cs-video-label {
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 10px 18px;
          background: #1e2a38;
          color: #94a3b8;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .cs-video-inner {
          background: linear-gradient(160deg, #0f1923 0%, #1a2535 100%);
          min-height: 340px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 16px;
          padding: 40px 32px;
          text-align: center;
        }

        .cs-zoom-icon-wrap {
          margin-bottom: 4px;
          filter: drop-shadow(0 4px 16px rgba(45,140,255,0.4));
        }

        .cs-video-title {
          font-size: 22px;
          font-weight: 700;
          color: white;
          margin: 0;
        }

        .cs-video-desc {
          font-size: 13.5px;
          color: #94a3b8;
          line-height: 1.6;
          max-width: 380px;
          margin: 0;
        }

        .cs-identity-notice {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 18px;
          background: rgba(45, 55, 72, 0.5);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 10px;
          color: #cbd5e1;
          font-size: 13px;
          line-height: 1.5;
          max-width: 420px;
          text-align: left;
          margin-top: 4px;
        }

        .cs-identity-notice strong {
          color: #e2e8f0;
        }

        /* ── Card ── */
        .cs-card {
          background: white;
          border-radius: 12px;
          border: 1px solid var(--border);
          padding: 18px 20px;
        }

        .cs-card-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 14px;
        }

        .cs-card-header h3 {
          font-size: 15px;
          font-weight: 600;
          color: var(--text-dark);
          margin: 0;
        }

        /* ── Detail grid ── */
        .cs-detail-grid {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .cs-detail-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 9px 12px;
          background: #f8f9fb;
          border-radius: 8px;
        }

        .cs-detail-label {
          font-size: 13px;
          color: var(--text-muted);
          min-width: 160px;
          font-weight: 500;
        }

        .cs-detail-val {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: var(--text-dark);
          font-weight: 500;
        }

        .cs-urgent {
          color: #dc2626 !important;
        }

        /* ── Connect button card ── */
        .cs-connect-card {
          background: white;
          border-radius: 12px;
          border: 1px solid var(--border);
          padding: 18px 20px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .cs-pwd-row {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 13px;
          color: var(--text-muted);
          padding: 8px 12px;
          background: #f8f9fb;
          border-radius: 8px;
        }

        .cs-pwd-row strong {
          color: var(--text-dark);
          font-weight: 700;
          letter-spacing: 1px;
        }

        .cs-connect-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 14px;
          background: #2D8CFF;
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          letter-spacing: 0.2px;
        }

        .cs-connect-btn:hover:not(:disabled) {
          background: #1a7aee;
          transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(45,140,255,0.35);
        }

        .cs-connect-btn:disabled {
          background: #e5e7eb;
          color: #9ca3af;
          cursor: not-allowed;
        }

        .cs-no-link {
          font-size: 12px;
          color: var(--text-muted);
          text-align: center;
          margin: 0;
          line-height: 1.5;
        }

        .cs-connected-msg {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: 12.5px;
          color: #16a34a;
          font-weight: 500;
          margin: 0;
        }

        /* ── Tips ── */
        .cs-tips {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .cs-tips li {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 13.5px;
          color: var(--text-dark);
          line-height: 1.5;
          padding: 9px 12px;
          background: #f8f9fb;
          border-radius: 8px;
        }

        .cs-tip-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--primary-navy);
          flex-shrink: 0;
          margin-top: 5px;
        }

        /* ── Session info ── */
        .cs-session-info-rows {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .cs-session-info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 7px 10px;
          background: #f8f9fb;
          border-radius: 7px;
          gap: 8px;
        }

        .cs-session-info-label {
          font-size: 12px;
          color: var(--text-muted);
          font-weight: 500;
          white-space: nowrap;
        }

        .cs-session-info-val {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-dark);
          text-align: right;
        }

        /* ── Rating ── */
        .cs-rated-msg {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: #16a34a;
        }

        .cs-stars {
          display: flex;
          gap: 4px;
        }

        .cs-star-btn {
          background: none;
          border: none;
          padding: 2px;
          cursor: pointer;
          transition: transform 0.15s;
        }

        .cs-star-btn:hover {
          transform: scale(1.15);
        }

        @media (max-width: 900px) {
          .cs-layout {
            grid-template-columns: 1fr;
          }
          .cs-sidebar {
            order: -1;
          }
          .cs-connect-card {
            order: -2;
          }
        }

        @media (max-width: 480px) {
          .pj-modal { padding: 20px 16px 16px; border-radius: 12px; }
          .pj-title { font-size: 17px; }
        }

        @media (max-width: 360px) {
          .pj-modal { padding: 16px 14px; }
          .pj-title { font-size: 16px; }
        }

        /* ── Pre-Join Modal ── */
        .pj-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.55);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
        }

        .pj-modal {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 480px;
          padding: 28px 28px 22px;
          display: flex;
          flex-direction: column;
          gap: 18px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }

        .pj-title {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 20px;
          font-weight: 700;
          color: var(--text-dark);
          margin: 0;
        }

        .pj-option {
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 16px 18px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .pj-option-primary {
          border-color: #bfdbfe;
          background: #eff6ff;
        }

        .pj-option-header {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .pj-option-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-dark);
        }

        .pj-badge {
          font-size: 10px;
          font-weight: 700;
          background: #2563eb;
          color: white;
          padding: 2px 8px;
          border-radius: 20px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .pj-option-desc {
          font-size: 13px;
          color: var(--text-muted);
          margin: 0;
          line-height: 1.5;
        }

        .pj-btn-primary {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 12px;
          background: #2D8CFF;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.15s;
        }

        .pj-btn-primary:hover {
          background: #1a7aee;
        }

        .pj-install-note {
          font-size: 12px;
          color: var(--text-muted);
          text-align: center;
          margin: 0;
        }

        .pj-install-note a {
          color: #2563eb;
          text-decoration: none;
        }

        .pj-install-note a:hover {
          text-decoration: underline;
        }

        .pj-device-check {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .pj-device-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 10px;
          background: #f8f9fb;
          border-radius: 7px;
          font-size: 13px;
          color: var(--text-dark);
          font-weight: 500;
        }

        .pj-device-row > span:nth-child(2) {
          flex: 1;
        }

        .pj-status {
          font-size: 12px;
          font-weight: 600;
        }

        .pj-idle { color: #9ca3af; }
        .pj-checking { color: #d97706; }
        .pj-ok { color: #16a34a; }
        .pj-denied { color: #dc2626; }

        .pj-fix-steps {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 12px 14px;
          font-size: 13px;
          color: #991b1b;
        }

        .pj-fix-steps strong {
          display: block;
          margin-bottom: 8px;
          font-size: 13px;
        }

        .pj-fix-steps ol {
          margin: 0;
          padding-left: 18px;
          line-height: 1.8;
        }

        .pj-browser-btns {
          display: flex;
          gap: 8px;
        }

        .pj-btn-check {
          flex: 1;
          padding: 9px;
          background: white;
          border: 1px solid var(--border);
          border-radius: 7px;
          font-size: 13px;
          font-weight: 600;
          color: var(--text-dark);
          cursor: pointer;
          transition: border-color 0.15s;
        }

        .pj-btn-check:hover:not(:disabled) {
          border-color: #2563eb;
          color: #2563eb;
        }

        .pj-btn-check:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .pj-btn-browser {
          flex: 1;
          padding: 9px;
          background: #f1f5f9;
          border: 1px solid var(--border);
          border-radius: 7px;
          font-size: 13px;
          font-weight: 600;
          color: var(--text-dark);
          cursor: pointer;
          transition: background 0.15s;
        }

        .pj-btn-browser:hover:not(:disabled) {
          background: #e2e8f0;
        }

        .pj-btn-browser:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pj-close {
          align-self: center;
          background: none;
          border: none;
          font-size: 13px;
          color: var(--text-muted);
          cursor: pointer;
          padding: 4px 8px;
        }

        .pj-close:hover {
          color: var(--text-dark);
        }
      `}</style>
    </div>
  )
}
